import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Quote } from "lucide-react";
import { BIG_TESTIMONIALS } from "./data";
import { FloatingCloud, FloatingMoon, Sparkle } from "./SleepDecorations";
import { LANDING } from "@/constants/testIds";

function TestimonialCard({ t, i, mobileCarousel = false }) {
  const initials = t.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <motion.div
      data-testid={LANDING.testimonialItem}
      initial={mobileCarousel ? false : { opacity: 0, y: 30 }}
      whileInView={mobileCarousel ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (i % 3) * 0.12 }}
      whileHover={mobileCarousel ? undefined : { y: -5 }}
      className={`h-full rounded-3xl border border-violet-200/60 bg-white/80 backdrop-blur-md p-7 shadow-[0_20px_50px_-15px_rgba(124,58,237,0.15)] hover:border-violet-400/60 hover:shadow-[0_25px_60px_-15px_rgba(124,58,237,0.25)] transition-all duration-500 flex flex-col ${
        !mobileCarousel && i === 4 ? "lg:col-span-1 lg:col-start-2" : ""
      }`}
    >
      <Quote className="w-7 h-7 text-[#a78bfa] mb-4" strokeWidth={1.5} />
      {/* Highlighted phrase comes FIRST for scannability */}
      <p className="font-display text-[#6d28d9] font-medium text-[17px] lg:text-lg leading-snug mb-4">
        &quot;{t.highlight}&quot;
      </p>
      <p className="text-slate-600 text-[14px] leading-relaxed mb-5 flex-1">
        {t.text}
      </p>
      <div className="pt-4 border-t border-violet-200/60 flex items-center gap-3">
        <span className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-300 to-violet-500 text-white font-semibold text-sm flex items-center justify-center shrink-0">
          {initials}
        </span>
        <div className="min-w-0">
          <p className="text-[#1a0a2e] font-semibold text-sm truncate">
            {t.name}
          </p>
          <p className="text-violet-600/80 text-xs mt-0.5 truncate">{t.role}</p>
        </div>
      </div>
    </motion.div>
  );
}

export default function TestimonialsBig() {
  const scrollerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  // Track which testimonial is centered to update the indicator dots.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      const center = el.scrollLeft + el.clientWidth / 2;
      const slides = el.querySelectorAll("[data-slide]");
      let best = 0;
      let bestDist = Infinity;
      slides.forEach((slide, idx) => {
        const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
        const d = Math.abs(slideCenter - center);
        if (d < bestDist) {
          bestDist = d;
          best = idx;
        }
      });
      setActiveIndex(best);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const goToSlide = (idx) => {
    const el = scrollerRef.current;
    if (!el) return;
    const slide = el.querySelectorAll("[data-slide]")[idx];
    if (slide) {
      el.scrollTo({
        left: slide.offsetLeft - (el.clientWidth - slide.clientWidth) / 2,
        behavior: "smooth",
      });
    }
  };

  return (
    <section className="relative py-28 lg:py-32 bg-[#faf6f0] overflow-hidden">
      {/* Sleep decorations */}
      <FloatingMoon
        className="absolute top-12 left-[-40px]"
        size={220}
        tone="dark"
      />
      <FloatingCloud
        className="absolute top-32 right-[-40px] text-[#6d28d9] drift-slow"
        size={220}
      />
      <Sparkle
        className="absolute top-1/3 right-[12%] text-rose-500/50"
        size={24}
      />
      <Sparkle
        className="absolute bottom-1/4 left-[8%] text-violet-500"
        size={20}
      />
      <FloatingCloud
        className="absolute bottom-0 left-[20%] text-violet-200 drift-slow"
        size={180}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mx-auto mb-12 lg:mb-14"
        >
          <h2 className="font-display text-4xl lg:text-[52px] text-[#1a0a2e] leading-[1.1]">
            O que muda na prática
            <span className="block text-[#6d28d9] font-medium mt-3">
              quando a mãe entende
            </span>
            <span className="block text-[#6d28d9] font-medium">
              o que está causando os despertares.
            </span>
          </h2>
        </motion.div>

        {/* DESKTOP / TABLET grid — unchanged */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7 items-stretch">
          {BIG_TESTIMONIALS.map((t, i) => (
            <TestimonialCard key={i} t={t} i={i} />
          ))}
        </div>

        {/* MOBILE carousel — horizontal snap scroll with peek + indicator */}
        <div className="md:hidden -mx-6">
          <div
            ref={scrollerRef}
            data-testid="testimonials-mobile-carousel"
            role="region"
            aria-label="Carrossel de depoimentos"
            tabIndex={0}
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth px-6 pb-3 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            style={{
              scrollPaddingLeft: "1.5rem",
              scrollPaddingRight: "1.5rem",
              overscrollBehaviorX: "contain",
            }}
          >
            {BIG_TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                data-slide
                aria-roledescription="slide"
                aria-label={`Depoimento ${i + 1} de ${BIG_TESTIMONIALS.length} — ${t.name}`}
                className="snap-center shrink-0 w-[86%] max-w-[340px] first:ml-[7%] last:mr-[7%]"
              >
                <TestimonialCard t={t} i={i} mobileCarousel />
              </div>
            ))}
          </div>

          {/* Swipe hint + dot indicator */}
          <div className="mt-6 flex flex-col items-center gap-3">
            <div
              className="flex items-center gap-1.5"
              role="tablist"
              aria-label="Selecionar depoimento"
            >
              {BIG_TESTIMONIALS.map((t, idx) => {
                const active = idx === activeIndex;
                return (
                  <button
                    key={idx}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    aria-label={`Ir para depoimento de ${t.name}`}
                    onClick={() => goToSlide(idx)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      active
                        ? "w-7 bg-[#6d28d9]"
                        : "w-1.5 bg-[#6d28d9]/25 hover:bg-[#6d28d9]/50"
                    }`}
                  />
                );
              })}
            </div>
            <p className="text-[11px] tracking-[0.22em] uppercase text-[#6d28d9]/80 font-bold flex items-center gap-2">
              <span aria-hidden="true">←</span>
              Arraste para ver os {BIG_TESTIMONIALS.length} depoimentos
              <span aria-hidden="true">→</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
